// =============================================================================
//  Copyright   : 2020 semantics GmbH
//  Date        : $Date: 2026-06-11 16:20:42 +0200 (Thu, 11 Jun 2026) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 163752 $
//  Purpose     : support for different deployment variants
// =============================================================================
// ---Third-party imports
// ---Framework imports
import { dict, strip, flatten, keys } from 'ldash';
import log from 'uix/logging';
// ---Globals
// not importable here: import {isSSR} from 'uix/utils'
const isSSR = process.env.UIX_ENV == 'express';
// rules for ServerChooser
const CUSTOM_PREFIX = {
    'https://visuallibrary.net': 'vl',
    'https://forschung.tmw.at': 'tmw',
    'https://sammlungen.ulb.uni-muenster.de': 'ulbms',
    'https://www.digital.wienbibliothek.at': 'wbr',
    'https://digital.wagnermuseum.de': 'rwm',
    'https://noah.nrw': 'noah',
    'https://stama.prod.vls.io': 'stama',
    'https://epflicht.ulb.uni-bonn.de': 'ulbbnpc',
    'https://e-pflicht.ub.uni-duesseldorf.de': 'ulbdpc',
    'https://zeitpunkt.nrw/': 'zdiginrw',
    'https://digital-test.obvsg.at': 'obv_stage',
    'https://collections.fes.de': 'fes',
    'https://openscience.mumok.at': 'mumok',
    'https://sammlung.volkskundemuseum.at': 'vkmw',
    'https://danrw.test.vls.io': 'danrw',
    'https://digital.ub.uni-potsdam.de': 'ubp',
    'https://digital.francke-halle.de': 'fshal',
    'https://ce.visuallibrary.net': 'nrwce'
};
// DEV - autoswitch between 127.0.0.1 and localhost
// SameSite cookie rules can break if npm-dev server is acessed on localhost and API requests go to 127.0.0.1 (another host)
export const LOCAL_HOSTNAME = isSSR ? 'localhost' : window.location.hostname;
// better user explicit ipv4 localhost addresses to prevent ipv6 dns slowdowns
export const KNOWN_SERVERS = [
    ...keys(CUSTOM_PREFIX)
];
export const apiurlToPrefix = (url)=>CUSTOM_PREFIX[url] || url.split('//').slice(-1)[0].replace(':', '').replace('.', '_').split('.')[0];
export const matchApiUrl = (pathPrefix)=>{
    if (pathPrefix) {
        let lo_match = pathPrefix.match(/^lo(\d*)$/);
        if (lo_match) {
            // lo180 => localhost:180
            return {
                apiurl: `http://${LOCAL_HOSTNAME}:${lo_match[1]}`,
                pathPrefix
            };
        }
        let rc_match = pathPrefix.match(/^([_\w]*)_(rc|dev|test)$/);
        if (rc_match) {
            // s2wp_dev => s2wp.dev.vls.io
            // s2wp_xxx_dev => s2wp.xxx.dev.vls.io
            return {
                apiurl: `https://${rc_match[1].replace('_', '.')}.${rc_match[2]}.vls.io`,
                pathPrefix
            };
        }
        for (let apiurl of KNOWN_SERVERS){
            let url_normalized = apiurlToPrefix(apiurl);
            //~ log(url_normalized, pathPrefix)
            if (url_normalized === pathPrefix) {
                return {
                    apiurl,
                    pathPrefix: url_normalized
                };
            }
        }
    }
    return {};
};
export const cleanPathPrefix = (pathPrefix, pathname)=>pathPrefix && pathname.startsWith(`/${pathPrefix}`) ? pathname.slice(1 + pathPrefix.length) : pathname;
export const pathPrefixIntercept = (deployData, pathname, verbose = true)=>{
    /* check pathname for a pathPrefix/apiUrl info */ if (deployData.mode == 'npm') {
        if (pathname) {
            let pathPrefix = strip(pathname, '/').split('/')[0];
            let { apiurl, pathPrefix: newPathPrefix } = matchApiUrl(pathPrefix);
            if (apiurl) {
                if (verbose) {
                    log.warn('switching apiurl %o -> %o', pathPrefix, apiurl);
                }
                deployData = {
                    ...deployData,
                    apiurl,
                    pathPrefix: newPathPrefix
                };
            }
        }
    }
    return {
        pathname: cleanPathPrefix(deployData.pathPrefix, pathname),
        deployData
    };
};
// uix-ssr comes from vls.spa.PACKS
//maybe use express server /setSessionId/
const EXPRESS_DEPLOY_CFG = {
    domain: '',
    pathPrefix: '',
    mode: 'ssr',
    package: 'uix-ssr'
};
export const getDeployConfig = ()=>{
    if (isSSR) {
        return EXPRESS_DEPLOY_CFG;
    } else {
        let r = document.getElementById('root');
        let datakeys = [
            'domain',
            'pathPrefix',
            'mode',
            'package',
            'version'
        ];
        return dict(datakeys.map((e)=>[
                e,
                r.getAttribute(`data-vl-${e}`)
            ]));
    }
};
export const getDevModePathPrefix = ()=>{
    let deployData = getDeployConfig();
    if (deployData.mode == 'npm') {
        let pathname = isSSR ? window.location.pathname : '';
        let intercept = pathPrefixIntercept(deployData, pathname, false);
        return intercept.deployData.pathPrefix;
    }
};
